import { Link } from "react-router-dom";
import { Users, UserCheck, Clock, Ban, Briefcase, CheckCircle2, IndianRupee, Wallet, Percent, Hourglass, Tags } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { PageHeader, StatCard, LoadingGrid, ErrorState, EmptyState, Avatar, StatusBadge } from "@/components/common";
import { inr, ago } from "@/lib/format";

export function CategoryCards({ categories, testprefix = "category-card" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3 stagger" data-testid="category-stats">
      {categories.map((c) => (
        <Link key={c.id} to={`/admin/creators?category=${c.id}`} className="uc-card uc-card-hover p-4" data-testid={`${testprefix}-${c.name.toLowerCase()}`}>
          <p className="text-sm font-medium truncate">{c.name}</p>
          <p className="num text-2xl mt-2" data-testid={`${testprefix}-${c.name.toLowerCase()}-count`}>{c.creator_count}</p>
          <p className="text-[11px] text-muted-foreground">{c.creator_count === 1 ? "Creator" : "Creators"}</p>
        </Link>
      ))}
    </div>
  );
}

export default function AdminOverview() {
  const { data: d, loading, error, reload } = useApi("/admin/overview");
  if (loading) return <LoadingGrid count={10} className="grid grid-cols-2 lg:grid-cols-5 gap-4" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-10">
      <PageHeader eyebrow="Admin" title="Overview" desc="Live figures calculated from your database." />
      <section>
        <p className="eyebrow mb-4">Creators</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger">
          <StatCard label="Total creators" value={d.total_creators} icon={Users} testid="ov-total-creators" />
          <StatCard label="Active" value={d.active_creators} icon={UserCheck} testid="ov-active-creators" />
          <StatCard label="Pending" value={d.pending_creators} icon={Clock} testid="ov-pending-creators" />
          <StatCard label="Suspended" value={d.suspended_creators} icon={Ban} testid="ov-suspended-creators" />
        </div>
      </section>
      <section>
        <p className="eyebrow mb-4">Deals & money</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 stagger">
          <StatCard label="Active deals" value={d.active_deals} icon={Briefcase} testid="ov-active-deals" />
          <StatCard label="Completed deals" value={d.completed_deals} icon={CheckCircle2} testid="ov-completed-deals" />
          <StatCard label="Total deal value" value={inr(d.total_deal_value)} icon={IndianRupee} testid="ov-total-deal-value" accent />
          <StatCard label="Creator payout" value={inr(d.total_creator_payout)} icon={Wallet} testid="ov-total-payout" />
          <StatCard label="Commission" value={inr(d.total_commission)} icon={Percent} testid="ov-total-commission" />
          <StatCard label="Pending payments" value={inr(d.pending_payments)} icon={Hourglass} testid="ov-pending-payments" />
        </div>
      </section>
      <section>
        <div className="flex items-center justify-between mb-4">
          <p className="eyebrow">Creators by category</p>
          <Link to="/admin/categories" className="text-xs text-primary hover:underline" data-testid="manage-categories-link">Manage</Link>
        </div>
        {d.categories.length ? <CategoryCards categories={d.categories} /> : <EmptyState icon={Tags} title="No categories" />}
      </section>
      <section>
        <div className="flex items-center justify-between mb-4">
          <p className="eyebrow">Latest signups</p>
          <Link to="/admin/creators" className="text-xs text-primary hover:underline" data-testid="view-all-creators-link">View all</Link>
        </div>
        {d.recent_creators.length === 0 ? <EmptyState icon={Users} title="No creators have joined yet." testid="overview-creators-empty" /> : (
          <div className="uc-card divide-y divide-border">
            {d.recent_creators.map((c) => (
              <Link key={c.user_id} to={`/admin/creators/${c.user_id}`} className="flex items-center gap-4 p-4 hover:bg-secondary/40 transition-colors" data-testid={`overview-creator-${c.user_id}`}>
                <Avatar src={c.profile_picture} name={c.full_name} />
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{c.full_name}</p><p className="text-xs text-muted-foreground">@{c.instagram_username} · {c.category_name}</p></div>
                <span className="text-xs text-muted-foreground hidden sm:block">{ago(c.joined_at)}</span>
                <StatusBadge status={c.status} />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
